import { __assign, __extends } from "tslib";
import React from 'react';
import { GALLERY_CONSTS, window, utils } from '@wix/pro-gallery-lib-old';
import { getStyle } from './getStyle';
// PG-1620: Pro Gallery plays Vimeo programmatically, which reaches the player via a
// cross-origin postMessage — so the user gesture is lost and iOS strips the audio /
// rejects the first play. For inline-click Vimeo we instead render the real Vimeo
// iframe: on mobile the tap lands inside it (a genuine gesture → sound), and on
// desktop we drive play programmatically (allowed there, no Vimeo chrome needed).
export function isVimeoUrl(url) {
    return !!url && url.includes('vimeo.com');
}
// The only mode that plays a video inline on click. Used by videoItemWrapper to
// enable the click-through, and (with transparentClickThrough) by videoItem to pick
// the native path — keep both call sites in sync via this single predicate.
export function isInlineVimeoVideo(props) {
    var options = props.options || {};
    return (isVimeoUrl(props.videoUrl || props.url) &&
        options.videoPlay === GALLERY_CONSTS.videoPlay.ON_CLICK &&
        options.itemClick === GALLERY_CONSTS.itemClick.NOTHING);
}
export function shouldUseNativeVimeo(props) {
    return isInlineVimeoVideo(props) && !!props.transparentClickThrough;
}
function parseVimeoUrl(url) {
    if (!url) {
        return null;
    }
    var idMatch = url.match(/(?:www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/[^/]*\/videos\/|album\/\d+\/video\/|video\/|)(\d+)/);
    if (!idMatch) {
        return null;
    }
    var hashMatch = url.match(/[?&]h=([^&]+)/);
    return { id: idMatch[1], hash: hashMatch ? hashMatch[1] : null };
}
function buildVimeoEmbedUrl(props) {
    var parsed = parseVimeoUrl(props.videoUrl);
    if (!parsed) {
        return null;
    }
    var params = new URLSearchParams({
        playsinline: '1',
        prefer_mms: '0',
        muted: props.options.videoSound ? '0' : '1',
        loop: props.options.videoLoop ? '1' : '0',
        // Mobile shows Vimeo's controls so the tap hits a real in-iframe play button
        // (required for sound on iOS); desktop hides them and plays programmatically.
        controls: utils.isMobile() || utils.isiOS() ? '1' : '0',
        title: '0',
        byline: '0',
        portrait: '0',
    });
    if (parsed.hash) {
        params.set('h', parsed.hash);
    }
    return "https://player.vimeo.com/video/".concat(parsed.id, "?").concat(params.toString());
}
var isDesktop = function () { return !utils.isMobile() && !utils.isiOS(); };
var NativeVimeoPlayer = /** @class */ (function (_super) {
    __extends(NativeVimeoPlayer, _super);
    function NativeVimeoPlayer(props) {
        var _this = _super.call(this, props) || this;
        _this.isCompMounted = false;
        _this.isPlayerReady = false;
        _this.isPlaying = false;
        _this.vimeoPlayer = null;
        _this.vimeoIframeEl = null;
        _this.biReported = false;
        _this.initPlayer = _this.initPlayer.bind(_this);
        _this.ensureSdkAndInit = _this.ensureSdkAndInit.bind(_this);
        return _this;
    }
    NativeVimeoPlayer.prototype.componentDidMount = function () {
        this.isCompMounted = true;
        this.ensureSdkAndInit();
    };
    NativeVimeoPlayer.prototype.componentWillUnmount = function () {
        this.isCompMounted = false;
        this.isPlayerReady = false;
        this.vimeoPlayer = null;
        this.vimeoIframeEl = null;
    };
    NativeVimeoPlayer.prototype.componentDidUpdate = function () {
        if (!this.vimeoPlayer) {
            this.ensureSdkAndInit();
            return;
        }
        if (!this.isPlayerReady) {
            return;
        }
        var isPlayingItem = this.props.playingVideoIdx === this.props.idx;
        if (!isPlayingItem && this.isPlaying) {
            // Pause when another item becomes the playing one (pausing needs no gesture).
            this.vimeoPlayer.pause().catch(function () { });
        }
        else if (isPlayingItem && !this.isPlaying && isDesktop()) {
            // Desktop: play programmatically when the gallery selects this item.
            this.vimeoPlayer.play().catch(function () { });
        }
    };
    NativeVimeoPlayer.prototype.hasSdk = function () {
        return !!(window && window.Vimeo && window.Vimeo.Player);
    };
    NativeVimeoPlayer.prototype.ensureSdkAndInit = function () {
        var _this = this;
        if (this.hasSdk()) {
            if (this.vimeoIframeEl) {
                this.initPlayer(this.vimeoIframeEl);
            }
            return;
        }
        import(
        /* webpackChunkName: "proGallery_vimeoPlayer" */ '@vimeo/player').then(function (Player) {
            window.Vimeo = window.Vimeo || { Player: Player.default };
            if (_this.isCompMounted && _this.vimeoIframeEl) {
                _this.initPlayer(_this.vimeoIframeEl);
            }
        });
    };
    NativeVimeoPlayer.prototype.initPlayer = function (iframe) {
        var _this = this;
        if (!iframe || this.vimeoPlayer || !this.isCompMounted || !this.hasSdk()) {
            return;
        }
        try {
            this.vimeoPlayer = new window.Vimeo.Player(iframe);
            this.vimeoPlayer
                .ready()
                .then(function () {
                if (!_this.isCompMounted) {
                    return;
                }
                _this.isPlayerReady = true;
                _this.props.actions.setItemLoaded();
                _this.props.onReady && _this.props.onReady();
                // Desktop: start now if this item was selected to play before SDK load.
                if (isDesktop() && _this.props.playingVideoIdx === _this.props.idx) {
                    _this.vimeoPlayer.play().catch(function () { });
                }
            })
                .catch(function () { });
            this.vimeoPlayer.on('play', function () {
                _this.isPlaying = true;
                _this.props.onPlayingChange && _this.props.onPlayingChange(true);
                // The in-iframe tap doesn't bubble to the gallery, so surface the events its
                // click flow normally would: VIDEO_PLAYED (coordination) + ITEM_CLICKED (BI).
                _this.props.actions.eventsListener(GALLERY_CONSTS.events.VIDEO_PLAYED, _this.props);
                if (!_this.biReported) {
                    _this.biReported = true;
                    _this.props.actions.eventsListener(GALLERY_CONSTS.events.ITEM_CLICKED, _this.props);
                }
            });
            this.vimeoPlayer.on('pause', function () {
                _this.isPlaying = false;
                _this.props.onPlayingChange && _this.props.onPlayingChange(false);
                // Reset the gallery's currentPlayingIdx so coordination stays in sync.
                _this.props.actions.eventsListener(GALLERY_CONSTS.events.VIDEO_PAUSED, _this.props);
            });
            this.vimeoPlayer.on('ended', function () {
                _this.isPlaying = false;
                _this.props.onPlayingChange && _this.props.onPlayingChange(false);
                _this.props.actions.eventsListener(GALLERY_CONSTS.events.VIDEO_ENDED, _this.props);
            });
        }
        catch (e) {
            console.error('[VIDEO] Could not init native Vimeo player', e);
        }
    };
    NativeVimeoPlayer.prototype.render = function () {
        var _this = this;
        if (!(this.props.loadVideo || this.props.playing)) {
            return null;
        }
        var embedUrl = buildVimeoEmbedUrl(this.props);
        if (!embedUrl) {
            return null;
        }
        var isWiderThenContainer = this.props.style.ratio >= this.props.cropRatio;
        var isCrop = this.props.options.cubeImages && this.props.options.cubeType === 'fill';
        return (React.createElement("iframe", { key: 'video-' + this.props.id, id: "video-".concat(this.props.id), className: 'gallery-item-visible video gallery-item', title: typeof this.props.alt === 'string' ? this.props.alt : 'untitled video', src: embedUrl, allow: "autoplay; fullscreen; picture-in-picture", playsInline: true, style: __assign(__assign({}, getStyle(isCrop, isWiderThenContainer)), { width: '100%', height: '100%', border: 0, pointerEvents: 'auto' }), ref: function (iframe) {
                _this.vimeoIframeEl = iframe;
                if (iframe) {
                    _this.ensureSdkAndInit();
                }
            } }));
    };
    return NativeVimeoPlayer;
}(React.Component));
export default NativeVimeoPlayer;
//# sourceMappingURL=nativeVimeoPlayer.js.map